import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getContractsDetails } from '../../../../redux/Slices/ContractSlice';

const ViewContract = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        const payload = await dispatch(getContractsDetails(id)).unwrap();
        setDetails(payload?.data || payload);
      } catch (error) {
        toast.error(error || t('contracts.view.messages.fetchError'), { rtl: isRTL });
        navigate('/contracts');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [dispatch, id, navigate, t, isRTL]);

  if (isLoading) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('contracts.view.loading')}</div>
      </div>
    );
  }

  if (!details) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('contracts.view.noData')}</div>
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, direction: isRTL ? 'rtl' : 'ltr' }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>{t('contracts.view.title')}</h2>
          <button style={secondaryButtonStyle} onClick={() => navigate('/contracts')}>
            {t('contracts.view.back')}
          </button>
        </div>
        <div style={detailsGrid}>
          <InfoRow label={t('contracts.view.contractNumber')} value={details.contract_no || details.code || details.id} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.internalCode')} value={details.code} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.project')} value={isRTL ? (details.project?.name_ar || details.project?.name_en) : (details.project?.name_en || details.project?.name_ar)} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.customer')} value={isRTL ? (details.customer?.name_ar || details.customer?.name_en) : (details.customer?.name_en || details.customer?.name_ar)} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.contractDate')} value={formatDate(details.date, isRTL)} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.startDate')} value={formatDate(details.start_date, isRTL)} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.endDate')} value={formatDate(details.end_date, isRTL)} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.retentionPercent')} value={`${details.retention_percent ?? 0}%`} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.advancePaymentPercent')} value={`${details.advance_payment_percent ?? 0}%`} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.status')} value={renderStatus(details.status, t)} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.totalAmount')} value={details.total_amount} isRTL={isRTL} />
          <InfoRow label={t('contracts.view.notes')} value={details.notes} isRTL={isRTL} />
        </div>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>{t('contracts.view.projectDetails')}</h3>
          <div style={detailsGrid}>
            <InfoRow label={t('contracts.view.projectCode')} value={details.project?.code} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.projectStatus')} value={renderProjectStatus(details.project?.status, t)} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.projectStartDate')} value={formatDate(details.project?.start_date, isRTL)} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.projectEndDate')} value={formatDate(details.project?.end_date, isRTL)} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.contractValue')} value={details.project?.contract_value} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.projectNotes')} value={details.project?.notes} isRTL={isRTL} />
          </div>
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>{t('contracts.view.customerDetails')}</h3>
          <div style={detailsGrid}>
            <InfoRow label={t('contracts.view.customerCode')} value={details.customer?.code} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.email')} value={details.customer?.email} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.phone')} value={details.customer?.phone} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.contactPerson')} value={details.customer?.contact_person} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.address')} value={details.customer?.address} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.taxNumber')} value={details.customer?.tax_number} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.customerNotes')} value={details.customer?.notes} isRTL={isRTL} />
          </div>
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>{t('contracts.view.quotationDetails')}</h3>
          <div style={detailsGrid}>
            <InfoRow label={t('contracts.view.quotationNumber')} value={details.quotation?.quotation_no} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.subtotalAmount')} value={details.quotation?.subtotal_amount} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.vatAmount')} value={details.quotation?.vat_amount} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.quotationTotalAmount')} value={details.quotation?.total_amount} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.status')} value={renderQuotationStatus(details.quotation?.status, t)} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.quotationDate')} value={formatDate(details.quotation?.sent_at, isRTL)} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.quotationDate')} value={formatDate(details.quotation?.approved_at, isRTL)} isRTL={isRTL} />
            <InfoRow label={t('contracts.view.quotationNotes')} value={details.quotation?.notes} isRTL={isRTL} />
          </div>
        </section>

        <section style={sectionStyle}>
          <h3 style={sectionTitleStyle}>{t('contracts.view.items.title')}</h3>
          <table style={itemsTableStyle}>
            <thead>
              <tr style={itemsHeaderRowStyle}>
                <th style={itemsHeaderCellStyle}>{t('contracts.view.items.descriptionAr')}</th>
                <th style={itemsHeaderCellStyle}>{t('contracts.view.items.descriptionEn')}</th>
                <th style={itemsHeaderCellStyle}>{t('contracts.view.items.quantity')}</th>
                <th style={itemsHeaderCellStyle}>{t('contracts.view.items.unitPrice')}</th>
                <th style={itemsHeaderCellStyle}>{t('contracts.view.items.total')}</th>
                <th style={itemsHeaderCellStyle}>{t('contracts.view.items.unit')}</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(details.items) && details.items.length > 0 ? (
                details.items.map((item) => (
                  <tr key={item.id}>
                    <td style={{ ...itemsTableCell, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{item.description_ar || '—'}</td>
                    <td style={{ ...itemsTableCell, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{item.description_en || '—'}</td>
                    <td style={{ ...itemsTableCell, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{item.quantity || '—'}</td>
                    <td style={{ ...itemsTableCell, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{item.unit_price || '—'}</td>
                    <td style={{ ...itemsTableCell, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{item.total_price || '—'}</td>
                    <td style={{ ...itemsTableCell, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{item.unit || '—'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ ...itemsTableCell, textAlign: 'center' }}>
                    {t('contracts.view.items.noItems')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
};

const renderStatus = (status, t) => {
  switch (status) {
    case 'active':
      return t('contracts.view.statusValues.active');
    case 'completed':
      return t('contracts.view.statusValues.completed');
    case 'cancelled':
      return t('contracts.view.statusValues.cancelled');
    default:
      return t('contracts.view.statusValues.draft');
  }
};

const InfoRow = ({ label, value, isRTL = true }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
    <span style={{ color: '#888', fontSize: 13 }}>{label}</span>
    <span style={{ fontWeight: 'bold' }}>{value || '—'}</span>
  </div>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
};

const cardStyle = {
  width: '100%',
  maxWidth: 900,
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
};

const detailsGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: 16,
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  padding: '10px 18px',
  borderRadius: 8,
  cursor: 'pointer',
};

const formatDate = (value, isRTL = true) => {
  if (!value) return '—';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-US');
  } catch {
    return value;
  }
};

const sectionStyle = {
  marginTop: 24,
  borderTop: '1px solid #333',
  paddingTop: 16,
};

const sectionTitleStyle = {
  margin: '0 0 12px 0',
  fontSize: 16,
  color: '#fff',
};

const itemsTableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  border: '1px solid #333',
};

const itemsHeaderRowStyle = {
  backgroundColor: '#AC2000',
};

const itemsHeaderCellStyle = {
  border: '1px solid #333',
  padding: 12,
  color: '#fff',
  fontWeight: 'bold',
  textAlign: 'center',
  fontSize: 14,
};

const itemsTableCell = {
  border: '1px solid #333',
  padding: 10,
  color: '#fff',
  fontSize: 14,
};

const renderProjectStatus = (status, t) => {
  switch (status) {
    case 'in_progress':
      return t('contracts.view.projectStatusValues.in_progress');
    case 'completed':
      return t('contracts.view.projectStatusValues.completed');
    case 'paused':
      return t('contracts.view.projectStatusValues.paused');
    default:
      return t('contracts.view.projectStatusValues.unknown');
  }
};

const renderQuotationStatus = (status, t) => {
  switch (status) {
    case 'approved':
      return t('contracts.view.quotationStatus.approved');
    case 'sent_to_client':
      return t('contracts.view.quotationStatus.sent_to_client');
    case 'draft':
    default:
      return t('contracts.view.quotationStatus.draft');
  }
};

export default ViewContract;

